import EmptyStateInactivePlan from "@/components/empty-state/EmptyStateInactivePlan.tsx";
import EmptyStateServerError from "@/components/empty-state/EmptyStateServerError.tsx";
import PasswordExpiryChecker from "@/components/password-expiry-checker/PasswordExpiryChecker.tsx";
import VersionUpdater from "@/components/version-updater/VersionUpdater.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { useAppConfig } from "@/queries/settings/use-app.query.ts";
import { DailyRefresherMount } from "@/services/daily-refresher/DailyRefresherMount.tsx";
import { useAuthStore } from "@/stores/employee.store.ts";
import { PortalAccessMode } from "@/types/employees/employee.types.ts";
import { ApiPlanStatus } from "@/types/subscription.types.ts";
import { FullScreenPage, ModalService } from "@skailer-com/uikit";
import { FunctionComponent, ReactElement } from "react";
import { Toaster } from "react-hot-toast";
import { Navigate, Outlet, useLocation } from "react-router-dom";

export const RequireAuth: FunctionComponent<{ children: ReactElement }> = ({ children }) => {
  const { token } = useAuthStore();
  const location = useLocation();

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

function App() {
  const { data: employeeData, isLoading, error } = useEmployeeMe();
  const isFullMode = employeeData?.portal_access_mode === PortalAccessMode.full;
  const { isLoading: appConfigIsLoading, error: appConfigError } = useAppConfig(isFullMode);

  if (isLoading || (isFullMode && appConfigIsLoading)) {
    return (
      <main className="main">
        <div className="main-page-loader-wrapper">
          <div className="main-page-loader"></div>
        </div>
      </main>
    );
  }
  if ((!isLoading && error) || (isFullMode && !appConfigIsLoading && appConfigError)) {
    return (
      <FullScreenPage>
        <EmptyStateServerError />
      </FullScreenPage>
    );
  }

  if (isFullMode && employeeData?.plan?.status === ApiPlanStatus.inactive) {
    return (
      <FullScreenPage>
        <EmptyStateInactivePlan />
      </FullScreenPage>
    );
  }

  return (
    <>
      <Outlet />
      <Toaster
        position="top-center"
        containerStyle={{ zIndex: 1700 }}
        toastOptions={{ duration: 4000 }}
      />
      <ModalService />
      <VersionUpdater />
      <DailyRefresherMount />
      {isFullMode && <PasswordExpiryChecker />}
    </>
  );
}

export default App;
